<script setup lang="ts">
import { type MaybeRef, computed, unref, useSlots } from 'vue';

const props = withDefaults(
  defineProps<{
    modelValue: string;
    type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';
    placeholder?: string;
    disabled?: boolean;
    autocomplete?: string;
    /** Validation error state - shows red border */
    error?: boolean;
    /** Optional error message shown below input */
    errorMessage?: MaybeRef<string>;
    /** Input name for forms */
    name?: string;
  }>(),
  {
    type: 'text',
    placeholder: '',
    disabled: false,
    autocomplete: '',
    error: false,
    errorMessage: '',
    name: '',
  },
);

// eslint-disable-next-line func-call-spacing
const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'blur'): void;
  (e: 'focus'): void;
}>();

const slots = useSlots();
const hasSuffix = computed(() => !!slots.suffix);

const inputValue = computed({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value),
});

const inputClass = computed(() => [
  'input w-full rounded-2xl border-2 bg-white px-8 py-16 text-16 text-off-black-500 placeholder:text-dark-grey-500',
  'transition-colors outline-none focus:border-teal-500',
  props.error ? 'border-red' : 'border-warm-grey-30',
  hasSuffix.value ? 'pr-48' : '',
]);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="relative flex w-full">
      <input
        v-model="inputValue"
        :type="type"
        :placeholder="placeholder"
        :disabled="disabled"
        :autocomplete="autocomplete"
        :name="name"
        :class="inputClass"
        class="!h-[48px] w-full !rounded-md !border-none !text-16 !shadow-none !outline-1 !outline-offset-2 focus:outline focus:outline-off-black-500"
        @blur="emit('blur')"
        @focus="emit('focus')"
      >
      <div
        v-if="$slots.suffix"
        class="absolute right-20 top-1/2 flex -translate-y-1/2 items-center"
      >
        <slot name="suffix" />
      </div>
    </div>
    <p
      v-if="error && unref(errorMessage)"
      class="text-12 text-red"
    >
      {{ unref(errorMessage) }}
    </p>
  </div>
</template>
